import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Sparkles,
  GraduationCap,
  CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROGRAM_OPTIONS = [
  "The B.L.A.S.T. Academy Workshop",
  "The Ai Advantage",
  "Young Hustlers Academy",
  "Open to any",
];

const SPECIALTY_OPTIONS = [
  // Media & Streaming
  "Podcasting",
  "Audio Production",
  "Video Production",
  "Livestreaming",
  "Photography",
  "Interviewing / Hosting",
  "Audio / Video Editing",
  "Broadcasting",
  // Ai & Tech
  "Prompt Engineering",
  "Ai Content Creation",
  "Ai for Business",
  "Web / App Development",
  "Graphic Design",
  "Digital Products",
  // Business & Life Skills
  "Branding",
  "Social Media Marketing",
  "Sales",
  "Financial Literacy",
  "Public Speaking",
  "Leadership Coaching",
  "Entrepreneurship",
  "Career Pathways / Trades",
  "Mentorship",
];

const AVAILABILITY_OPTIONS = [
  "Weekday mornings",
  "Weekday afternoons",
  "Weekday evenings",
  "Weekend mornings",
  "Weekend afternoons",
  "Weekend evenings",
];

const EXPERIENCE_OPTIONS = [
  "Less than 1 year",
  "1–3 years",
  "3–5 years",
  "5–10 years",
  "10+ years",
];

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  preferredName: "",
  email: "",
  phone: "",
  cityBorough: "",
  programs: [],
  yearsExperience: "",
  specialties: [],
  otherSpecialties: "",
  availability: [],
  bio: "",
  whyBlast: "",
  portfolioUrl: "",
  linkedinUrl: "",
  resumeUrl: "",
  instagramUrl: "",
  twitterUrl: "",
  tiktokUrl: "",
  youtubeUrl: "",
};

export default function InstructorSignup() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const toggleArr = (field, value) =>
    setForm((prev) => {
      const arr = prev[field];
      return {
        ...prev,
        [field]: arr.includes(value)
          ? arr.filter((v) => v !== value)
          : [...arr, value],
      };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() ||
        !form.phone.trim() || !form.cityBorough.trim() || !form.bio.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (form.bio.trim().length < 10) {
      toast.error("Bio should be at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/instructor-applications`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-[#FAF6EC] text-zinc-900 grain">
        <section className="pt-32 md:pt-44 pb-24 px-6">
          <div className="max-w-xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF2A2A] mb-6 shadow-[0_0_28px_rgba(255,42,42,0.4)]">
              <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={2.2} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.42em] text-[#FF2A2A] font-semibold mb-3">
              Application Received
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05] mb-5">
              Thanks for raising your hand.
            </h1>
            <p className="text-zinc-700 text-base md:text-lg leading-relaxed mb-8">
              We've received your instructor application and the B.L.A.S.T. team will review it personally.
              Expect to hear back within <span className="font-semibold text-zinc-900">5–7 business days</span>.
              In the meantime, feel free to explore the programs you'd potentially teach.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link
                to="/"
                data-testid="instructor-success-home"
                className="inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#E60000] text-white px-6 py-3.5 text-sm uppercase tracking-[0.18em] font-medium shadow-[0_0_28px_rgba(255,42,42,0.4)] transition-all"
              >
                Back Home
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/#programs"
                data-testid="instructor-success-programs"
                className="inline-flex items-center gap-2 border border-zinc-900/20 hover:border-zinc-900/50 hover:bg-zinc-900/5 text-zinc-900 px-6 py-3.5 text-sm uppercase tracking-[0.18em] font-medium transition-all"
              >
                See The Programs
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FAF6EC] text-zinc-900 grain">
      <section className="pt-28 sm:pt-32 md:pt-40 pb-20 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            data-testid="instructor-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-600 hover:text-zinc-900 transition-colors mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
          </Link>

          <div className="flex items-center gap-3 mb-5">
            <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-medium border border-[#FF2A2A]/50 text-[#FF2A2A] bg-[#FF2A2A]/5">
              Teaching Artists
            </span>
            <span className="h-px w-8 bg-[#FF2A2A]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-zinc-600 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Join The B.L.A.S.T. Team
            </span>
          </div>

          <h1
            data-testid="instructor-page-title"
            className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.02] mb-5"
          >
            Become an <span className="text-[#FF2A2A]">Instructor</span>.
          </h1>
          <p className="text-zinc-700 text-base md:text-lg leading-[1.7] mb-10 max-w-2xl">
            We're building a bench of professional teaching artists, creative-industry
            practitioners, and entrepreneurs to lead workshops across our programs
            that are expanding citywide. Tell us about you — we review every
            application personally.
          </p>

          <form
            onSubmit={handleSubmit}
            data-testid="instructor-form"
            className="space-y-6 bg-white border border-zinc-900/10 p-6 md:p-8"
          >
            {/* NAME */}
            <SectionHeader icon={GraduationCap} title="Your Name" />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="First name" required>
                <input
                  data-testid="instructor-firstname"
                  value={form.firstName}
                  onChange={(e) => update("firstName", e.target.value)}
                  className={inputCls}
                  placeholder="Jane"
                  required
                />
              </Field>
              <Field label="Last name" required>
                <input
                  data-testid="instructor-lastname"
                  value={form.lastName}
                  onChange={(e) => update("lastName", e.target.value)}
                  className={inputCls}
                  placeholder="Doe"
                  required
                />
              </Field>
            </div>
            <Field label="What do you prefer to be called? (optional)">
              <input
                data-testid="instructor-preferred-name"
                value={form.preferredName}
                onChange={(e) => update("preferredName", e.target.value)}
                className={inputCls}
                placeholder="e.g. Janie, DJ Spark, Coach J"
              />
            </Field>

            {/* CONTACT */}
            <SectionHeader title="Contact" />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Email" required>
                <input
                  data-testid="instructor-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className={inputCls}
                  placeholder="you@email.com"
                  required
                />
              </Field>
              <Field label="Phone" required>
                <input
                  data-testid="instructor-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className={inputCls}
                  placeholder="(555) 555-5555"
                  required
                />
              </Field>
              <Field label="City / Borough" required>
                <input
                  data-testid="instructor-city"
                  value={form.cityBorough}
                  onChange={(e) => update("cityBorough", e.target.value)}
                  className={inputCls}
                  placeholder="Brooklyn, NY"
                  required
                />
              </Field>
            </div>

            {/* PROGRAMS */}
            <SectionHeader title="Which program(s) would you like to teach?" />
            <div className="grid sm:grid-cols-2 gap-2">
              {PROGRAM_OPTIONS.map((p) => (
                <CheckChip
                  key={p}
                  testid={`instructor-program-${p.replace(/\s+/g, "-").toLowerCase()}`}
                  label={p}
                  checked={form.programs.includes(p)}
                  onToggle={() => toggleArr("programs", p)}
                />
              ))}
            </div>

            {/* EXPERIENCE */}
            <SectionHeader title="Experience" />
            <Field label="Years teaching / in your field">
              <select
                data-testid="instructor-years"
                value={form.yearsExperience}
                onChange={(e) => update("yearsExperience", e.target.value)}
                className={inputCls}
              >
                <option value="">Select…</option>
                {EXPERIENCE_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>

            {/* SPECIALTIES */}
            <SectionHeader title="Core specialties (select all that apply)" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {SPECIALTY_OPTIONS.map((s) => (
                <CheckChip
                  key={s}
                  testid={`instructor-spec-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  label={s}
                  checked={form.specialties.includes(s)}
                  onToggle={() => toggleArr("specialties", s)}
                />
              ))}
            </div>
            <Field label="Anything else? (optional)">
              <input
                data-testid="instructor-other-specialties"
                value={form.otherSpecialties}
                onChange={(e) => update("otherSpecialties", e.target.value)}
                className={inputCls}
                placeholder="Additional skills not listed above"
              />
            </Field>

            {/* AVAILABILITY */}
            <SectionHeader title="Availability (check all that apply)" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {AVAILABILITY_OPTIONS.map((a) => (
                <CheckChip
                  key={a}
                  testid={`instructor-avail-${a.replace(/\s+/g, "-").toLowerCase()}`}
                  label={a}
                  checked={form.availability.includes(a)}
                  onToggle={() => toggleArr("availability", a)}
                />
              ))}
            </div>

            {/* BIO */}
            <SectionHeader title="Tell us about you" />
            <Field label="Short bio / background" required>
              <textarea
                data-testid="instructor-bio"
                value={form.bio}
                onChange={(e) => update("bio", e.target.value)}
                className={`${inputCls} min-h-[120px] resize-y`}
                placeholder="What you do, where you've worked, what makes you good with young people…"
                required
                minLength={10}
              />
            </Field>
            <Field label="Why B.L.A.S.T.?">
              <textarea
                data-testid="instructor-why"
                value={form.whyBlast}
                onChange={(e) => update("whyBlast", e.target.value)}
                className={`${inputCls} min-h-[90px] resize-y`}
                placeholder="What draws you to our mission with NYC youth?"
              />
            </Field>

            {/* LINKS */}
            <SectionHeader title="Links (optional)" />
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Portfolio / Website">
                <input
                  data-testid="instructor-portfolio"
                  value={form.portfolioUrl}
                  onChange={(e) => update("portfolioUrl", e.target.value)}
                  className={inputCls}
                  placeholder="https://…"
                />
              </Field>
              <Field label="LinkedIn">
                <input
                  data-testid="instructor-linkedin"
                  value={form.linkedinUrl}
                  onChange={(e) => update("linkedinUrl", e.target.value)}
                  className={inputCls}
                  placeholder="linkedin.com/in/…"
                />
              </Field>
              <Field label="Resume (URL)">
                <input
                  data-testid="instructor-resume"
                  value={form.resumeUrl}
                  onChange={(e) => update("resumeUrl", e.target.value)}
                  className={inputCls}
                  placeholder="Drive / Dropbox link"
                />
              </Field>
            </div>

            {/* SOCIALS */}
            <SectionHeader title="Social media (optional)" />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Instagram">
                <input
                  data-testid="instructor-instagram"
                  value={form.instagramUrl}
                  onChange={(e) => update("instagramUrl", e.target.value)}
                  className={inputCls}
                  placeholder="@handle or instagram.com/…"
                />
              </Field>
              <Field label="Twitter / X">
                <input
                  data-testid="instructor-twitter"
                  value={form.twitterUrl}
                  onChange={(e) => update("twitterUrl", e.target.value)}
                  className={inputCls}
                  placeholder="@handle or x.com/…"
                />
              </Field>
              <Field label="TikTok">
                <input
                  data-testid="instructor-tiktok"
                  value={form.tiktokUrl}
                  onChange={(e) => update("tiktokUrl", e.target.value)}
                  className={inputCls}
                  placeholder="@handle or tiktok.com/@…"
                />
              </Field>
              <Field label="YouTube">
                <input
                  data-testid="instructor-youtube"
                  value={form.youtubeUrl}
                  onChange={(e) => update("youtubeUrl", e.target.value)}
                  className={inputCls}
                  placeholder="youtube.com/@…"
                />
              </Field>
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                data-testid="instructor-submit"
                className="group inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#E60000] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-3.5 text-sm uppercase tracking-[0.18em] font-medium shadow-[0_0_28px_rgba(255,42,42,0.4)] hover:shadow-[0_0_44px_rgba(255,42,42,0.6)] transition-all"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Submit Application
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
              <span className="text-xs text-zinc-500">
                We review every application personally.
              </span>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

const inputCls =
  "w-full bg-[#FAF6EC] border border-zinc-900/15 focus:border-[#FF2A2A] focus:ring-2 focus:ring-[#FF2A2A]/20 focus:outline-none px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 transition-all";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] text-zinc-600 font-semibold mb-2">
        {label}
        {required && <span className="text-[#FF2A2A] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 pt-3 border-t border-zinc-900/10 first:border-t-0 first:pt-0">
      {Icon && <Icon className="w-4 h-4 text-[#FF2A2A]" />}
      <h2 className="text-[11px] uppercase tracking-[0.3em] text-zinc-900 font-semibold">
        {title}
      </h2>
    </div>
  );
}

function CheckChip({ label, checked, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className={`text-left px-4 py-3 text-sm border transition-all ${
        checked
          ? "border-[#FF2A2A] bg-[#FF2A2A]/5 text-zinc-900"
          : "border-zinc-900/15 hover:border-zinc-900/40 text-zinc-700"
      }`}
    >
      <span className="inline-flex items-center gap-2">
        <span
          className={`inline-block w-3.5 h-3.5 border ${
            checked ? "bg-[#FF2A2A] border-[#FF2A2A]" : "border-zinc-900/30"
          }`}
        />
        {label}
      </span>
    </button>
  );
}
